// src/api/topicAPI.js
import api from "./api";

/**
 * English:
 * Topic/Suggestion/Flag APIs must use the shared `api` instance only.
 * - No base URL constants
 * - No API_URL import/export usage
 * - No Authorization/Bearer headers
 * - Cookie auth handled by withCredentials in api.js
 */

// Create a new topic (supports FormData with image)
export const addTopic = async (topicData) => {
  const config = {};

  // English: If uploading files, set multipart header
  if (topicData instanceof FormData) {
    config.headers = { "Content-Type": "multipart/form-data" };
  }

  const res = await api.post(`/api/topics/add`, topicData, config);
  return res.data;
};

export const getAllTopics = async () => {
  const res = await api.get(`/api/topics`);
  return res.data;
};

export const updateTopic = async (id, updatedFields) => {
  const res = await api.put(`/api/topics/${id}`, updatedFields, {
    headers: { "Content-Type": "application/json" },
  });
  return res.data;
};

export const getTopicById = async (id) => {
  const res = await api.get(`/api/topics/${id}`);
  return res.data;
};

export const getTopicByIdFresh = async (id) => {
  const res = await api.get(`/api/topics/${id}`, {
    params: { _ts: Date.now() },
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  const fresh = res.data;
  primeTopicCache(fresh);
  return fresh;
};

// English: Small in-memory topic cache to reduce repeat loading during navigation.
const topicCache = new Map();
const TOPIC_CACHE_TTL_MS = 30 * 1000;

function getTopicCacheKey(id) {
  return String(id);
}

// English: A topic is considered "full" only when the detail payload
// has enough fields for TopicView owner display + content rendering.
function hasFullTopicDetails(topic) {
  if (!topic || !topic.id) return false;

  const hasContent =
    typeof topic.past === "string" &&
    typeof topic.present === "string" &&
    typeof topic.future === "string";

  const hasOwnerIdentity =
    !!topic.creator_username ||
    !!topic.creator_name ||
    !!topic.created_by_username ||
    !!topic.created_by;

  return hasContent && hasOwnerIdentity;
}

export const primeTopicCache = (topic) => {
  if (!topic || !topic.id) return;

  const key = getTopicCacheKey(topic.id);
  const existing = topicCache.get(key);

  // English:
  // Never downgrade a full cached topic with a thinner partial payload.
  // Merge when cache already has richer data.
  const nextData =
    existing?.data && hasFullTopicDetails(existing.data) && !hasFullTopicDetails(topic)
      ? { ...existing.data, ...topic }
      : { ...(existing?.data || {}), ...topic };

  topicCache.set(key, {
    data: nextData,
    expiresAt: Date.now() + TOPIC_CACHE_TTL_MS,
  });
};

export const getTopicByIdCached = async (id) => {
  const key = getTopicCacheKey(id);
  const cached = topicCache.get(key);

  // English: Use cache only when it is still fresh AND already contains
  // the full topic detail payload needed by TopicView.
  if (cached && cached.expiresAt > Date.now() && hasFullTopicDetails(cached.data)) {
    return cached.data;
  }

  const fresh = await getTopicById(id);
  primeTopicCache(fresh);
  return fresh;
};

export const checkTopicDuplicate = async (word) => {
  // Send as query param so symbols like ?, #, /, + don't break routing
  const trimmed = (word || "").trim();

  const res = await api.get(`/api/topics/check`, {
    params: { word: trimmed }, // axios will encode safely
  });

  return res.data;
};

export const getMyTopics = async () => {
  const res = await api.get(`/api/topics/my-topics`);
  return res.data;
};

// Typesense-backed search
export const searchTopics = async ({ query, page = 1, limit = 10 }) => {
  const res = await api.get(`/api/search`, {
    params: { q: query, page, limit },
  });
  return res.data; // { topics, total, page, limit }
};

export const suggestTopics = async ({ query, limit = 10 }) => {
  const res = await api.get(`/api/search/suggest`, {
    params: { q: query, limit },
  });
  return res.data.suggestions || [];
};

export const getTrendingTopics = async (limit = 12) => {
  const res = await api.get(`/api/topics/trending`, { params: { limit } });
  return res.data.topics || [];
};

// Suggestions
export const addSuggestionForTopic = async (suggestionData) => {
  const res = await api.post(`/api/suggestions/add`, suggestionData);
  return res.data;
};

// Flags
export const flagTopic = async (flagData) => {
  const res = await api.post(`/api/flags/add`, flagData);
  return res.data;
};

export const getMyFlagStatusAPI = async (topicId) => {
  const res = await api.get(`/api/flags/status/${topicId}`);
  return res.data; // { flagged: boolean }
};

export const getMyFlagStatusBatchAPI = async (topicIds) => {
  const ids = Array.isArray(topicIds) ? topicIds : [];
  const qs = ids.join(",");

  const res = await api.get(`/api/flags/status-batch`, {
    params: { topic_ids: qs },
  });

  return res.data; // { flaggedMap: { "1": true/false, ... } }
};

export const getTopicByWord = async (word) => {
  const trimmed = (word || "").trim();
  if (!trimmed) return { exists: false, topic: null };

  // Send as query param so symbols like ?, #, /, + don't break routing
  const res = await api.get(`/api/topics/by-word`, {
    params: { word: trimmed }, // axios will encode safely
  });

  return res.data; // { exists, topic }
};

// English: Warm topic navigation in the background so first open feels faster.
// Use this on hover/focus for likely-next clicks.
export const warmTopicNavigation = async (topicOrWord) => {
  // Topic object case
  if (topicOrWord && typeof topicOrWord === "object") {
    primeTopicCache(topicOrWord);

    if (topicOrWord.id && !hasFullTopicDetails(topicOrWord)) {
      try {
        await getTopicByIdCached(topicOrWord.id);
      } catch {
        // English: Silent warmup only. Ignore background failures.
      }
    }

    return;
  }

  // Word/string case
  const cleanWord = String(topicOrWord || "").trim();
  if (!cleanWord) return;

  try {
    const data = await getTopicByWord(cleanWord);
    if (data?.exists && data?.topic) {
      primeTopicCache(data.topic);

      if (data.topic.id && !hasFullTopicDetails(data.topic)) {
        try {
          await getTopicByIdCached(data.topic.id);
        } catch {
          // English: Silent warmup only. Ignore background failures.
        }
      }
    }
  } catch {
    // English: Silent warmup only. Ignore background failures.
  }
};